/* Atelier Alpino (owner, 04/10): the information pages' behaviour, loaded by sections/main-page.liquid (layout "Page")
   and sections/multi-column.liquid (layout "Steps"). The returns page /pages/resi:
   - the return procedure (<luxe-return-steps>) as the story's timeline: its bar of numbered stops stays under the
     header while the steps go by, the line fills up to the step being read and the step read carries aria-current;
     a stop or the arrows bring a step to the reading line ("vada avanti sia con lo scroll down ... che con i bottoni");
   - the column under the title (.luxe-split__aside, built before the first paint by the inline script after the
     content in layout/theme.liquid) stays in place under the header from 1000px. Taller than the window, it moves with
     the page until its end shows (going down) or its top shows (going up), then stays: no scrollbar of its own;
   - the links to the withdrawal page lead to the withdrawal form in that column;
   - that form's choice list opens under its field (Shopify Forms opened it over the question when the window had no
     room below).
   Reduced motion: no smooth scrolling, the states still follow the reading. Without this script the column simply
   sits beside the page and the procedure's stops are links. */

const READ = 0.3; // the reading line, this far down what the header and the bar leave of the window
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const wide = matchMedia('(min-width: 1000px)');
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const scrollBehavior = () => (reduced.matches ? 'auto' : 'smooth');
// a length the stylesheet gives in px or rem, in px
function length(element, name, fallback) {
  const value = getComputedStyle(element).getPropertyValue(name).trim();
  const number = parseFloat(value);
  if (Number.isNaN(number)) return fallback;
  return value.endsWith('rem') ? number * parseFloat(getComputedStyle(document.documentElement).fontSize) : number;
}

// the header's lower edge on screen (it stays at the top; 0 if it has gone)
function headerEdge() {
  const header = document.querySelector('.shopify-section--header');
  if (header) return Math.max(0, header.getBoundingClientRect().bottom);
  return length(document.documentElement, '--header-height', 0);
}

/* ---------- the return procedure ---------- */
class LuxeReturnSteps extends HTMLElement {
  connectedCallback() {
    if (!this.items) this.setup();
    if (!this.items) return;
    addEventListener('scroll', this.schedule, { passive: true });
    addEventListener('resize', this.schedule);
    this.observer = new ResizeObserver(this.schedule);
    this.observer.observe(this);
    this.classList.add('is-live');
    this.update();
  }

  disconnectedCallback() {
    removeEventListener('scroll', this.schedule);
    removeEventListener('resize', this.schedule);
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  setup() {
    const bar = this.querySelector('.luxe-steps__bar');
    const stops = [...this.querySelectorAll('.luxe-steps__stop')];
    const items = [...this.querySelectorAll('.luxe-steps__item')];
    if (!bar || items.length < 2 || stops.length !== items.length) return;
    Object.assign(this, { bar, stops, items, current: -1, fill: -1, edge: -1, target: null, frame: 0 });
    this.prev = this.querySelector('[data-step-prev]');
    this.next = this.querySelector('[data-step-next]');
    this.at = this.querySelector('[data-step-at]');
    this.live = this.querySelector('[data-step-live]');
    this.schedule = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
    };
    stops.forEach((stop, index) => stop.addEventListener('click', (event) => {
      event.preventDefault();
      this.go(index, { focus: true });
    }));
    this.prev?.addEventListener('click', () => this.move(-1));
    this.next?.addEventListener('click', () => this.move(1));
    // the theme editor: a block chosen in the sidebar is brought to the reading line
    this.addEventListener('shopify:block:select', (event) => {
      const index = items.indexOf(event.target.closest('.luxe-steps__item'));
      if (index >= 0) this.go(index, { instant: true });
    });
  }

  // the stuck bar's height (the CSS folds the labels away once it sticks)
  slim() {
    return length(this, '--luxe-steps-slim', this.bar.offsetHeight);
  }

  readingLine(edge) {
    const bar = this.slim();
    return edge + bar + (innerHeight - edge - bar) * READ;
  }

  update() {
    this.frame = 0;
    // read everything first
    const edge = headerEdge();
    const barTop = this.bar.getBoundingClientRect().top;
    const tops = this.items.map((item) => item.getBoundingClientRect().top);
    const end = this.items[this.items.length - 1].getBoundingClientRect().bottom;
    const line = this.readingLine(edge);
    const slim = this.slim();
    // then write
    if (Math.abs(edge - this.edge) > 0.5) {
      this.edge = edge;
      this.style.setProperty('--luxe-steps-top', `${edge.toFixed(1)}px`);
    }
    let reading = 0;
    tops.forEach((top, index) => { if (top <= line) reading = index; });
    const next = tops[reading + 1];
    const part = next === undefined ? 0 : clamp((line - tops[reading]) / Math.max(1, next - tops[reading]), 0, 1);
    const fill = Math.round(((reading + part) / (this.items.length - 1)) * 1000) / 1000;
    if (fill !== this.fill) {
      this.fill = fill;
      this.style.setProperty('--luxe-steps-fill', fill);
    }
    const stuck = barTop <= edge + 1;
    this.bar.classList.toggle('is-stuck', stuck);
    this.bar.classList.toggle('is-done', stuck && end <= edge + slim + 8);
    if (this.target === reading) this.target = null;
    if (reading !== this.current) this.mark(reading);
  }

  mark(index) {
    this.current = index;
    this.stops.forEach((stop, i) => {
      stop.classList.toggle('is-reached', i <= index);
      if (i === index) stop.setAttribute('aria-current', 'step');
      else stop.removeAttribute('aria-current');
    });
    this.items.forEach((item, i) => item.classList.toggle('is-current', i === index));
    if (this.at) this.at.textContent = String(index + 1);
    this.prev?.setAttribute('aria-disabled', String(index <= 0));
    this.next?.setAttribute('aria-disabled', String(index >= this.items.length - 1));
  }

  // a step lands on the reading line (so it is the one being read); a stop also moves the focus to it
  go(index, { focus = false, instant = false } = {}) {
    const item = this.items[index];
    if (!item) return;
    const top = scrollY + item.getBoundingClientRect().top - this.readingLine(headerEdge()) + 2;
    this.target = index;
    clearTimeout(this.release);
    this.release = setTimeout(() => { this.target = null; }, 1200);
    scrollTo({ top: Math.max(0, Math.round(top)), behavior: instant ? 'auto' : scrollBehavior() });
    if (focus) {
      item.setAttribute('tabindex', '-1');
      item.focus({ preventScroll: true });
    }
  }

  // the arrows: the step before or after the one read (or the one already on its way)
  move(delta) {
    const from = this.target ?? this.current;
    const index = clamp(from + delta, 0, this.items.length - 1);
    if (index === from) return;
    this.go(index);
    if (this.live) this.live.textContent = `${index + 1}/${this.items.length} ${this.items[index].querySelector('.luxe-steps__title')?.innerText.trim() || ''}`;
  }
}

if (!customElements.get('luxe-return-steps')) customElements.define('luxe-return-steps', LuxeReturnSteps);

/* ---------- the column under the title ---------- */
function stickColumn(split) {
  const aside = split.querySelector(':scope > .luxe-split__aside');
  const body = split.querySelector(':scope > .luxe-split__body');
  if (!aside || !body || split.luxeStick) return;
  let mode = '';
  let lastY = scrollY;
  let frame = 0;

  const gap = () => length(split, '--luxe-split-gap', 32);
  const reset = () => {
    aside.style.removeProperty('margin-top');
    aside.style.removeProperty('top');
    aside.style.removeProperty('bottom');
    mode = '';
  };
  // from where the column is now on screen (so nothing jumps), the edge it waits for: its end going down, its top
  // going up
  const place = (next, top, height, room) => {
    const offset = clamp(aside.getBoundingClientRect().top - split.getBoundingClientRect().top, 0, room);
    aside.style.marginTop = `${offset.toFixed(1)}px`;
    if (next === 'down') {
      aside.style.top = `${(innerHeight - height - gap()).toFixed(1)}px`;
      aside.style.bottom = 'auto';
    } else {
      aside.style.top = 'auto';
      aside.style.bottom = `${(innerHeight - top - height).toFixed(1)}px`;
    }
    mode = next;
  };

  function update() {
    frame = 0;
    const y = scrollY;
    const direction = Math.sign(y - lastY);
    lastY = y;
    if (!wide.matches) {
      if (mode) reset();
      return;
    }
    const top = headerEdge() + gap();
    const height = aside.offsetHeight;
    const room = Math.max(0, body.offsetHeight - height);
    if (top + height + gap() <= innerHeight || !room) {
      if (mode !== 'fit') {
        reset();
        mode = 'fit';
      }
      aside.style.top = `${top.toFixed(1)}px`;
      return;
    }
    if (mode !== 'down' && mode !== 'up') place(direction < 0 ? 'up' : 'down', top, height, room);
    else if (direction > 0 && mode !== 'down') place('down', top, height, room);
    else if (direction < 0 && mode !== 'up') place('up', top, height, room);
    else if (mode === 'down') aside.style.top = `${(innerHeight - height - gap()).toFixed(1)}px`;
    else aside.style.bottom = `${(innerHeight - top - height).toFixed(1)}px`;
  }

  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  const observer = new ResizeObserver(schedule);
  observer.observe(aside);
  observer.observe(body);
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule);
  wide.addEventListener('change', schedule);
  split.luxeStick = true;
  update();
}

/* ---------- the withdrawal form in the column: the links to the withdrawal page lead to it ---------- */
function formLinks() {
  const form = document.querySelector('.luxe-split__aside .shopify-app-block [data-form-root]');
  const section = form?.closest('.shopify-section');
  if (!section) return;
  section.setAttribute('tabindex', '-1');
  document.querySelectorAll('a[href]').forEach((link) => {
    let url;
    try { url = new URL(link.href, location.href); } catch { return; }
    if (url.origin !== location.origin || !/\/pages\/recesso\/?$/.test(url.pathname)) return;
    link.setAttribute('href', `#${section.id}`);
    link.addEventListener('click', (event) => {
      event.preventDefault();
      section.scrollIntoView({ block: 'nearest', behavior: scrollBehavior() });
      section.focus({ preventScroll: true });
    });
  });
}

/* ---------- the withdrawal form's choice list (Shopify Forms, in its own shadow root): with no room under it, the
   list opens upwards from the top of the question, over the question's text and the field above it (owner, 05/10:
   the "Si" showed too high). It opens under its field instead. ---------- */
const FORMS_STYLE = '[class*="_dropdownContainer_"][class*="_showUpwards_"] { top: 100%; bottom: auto; margin: 4px 0 0; }';
function formsDropdown(attempt = 0) {
  const hosts = [...document.querySelectorAll('[data-form-root] shopify-forms-embed')];
  let waiting = hosts.length === 0;
  hosts.forEach((host) => {
    const root = host.shadowRoot;
    if (!root) { waiting = true; return; }
    if (root.querySelector('style[data-luxe-forms]')) return;
    const style = document.createElement('style');
    style.dataset.luxeForms = '';
    style.textContent = FORMS_STYLE;
    root.append(style);
  });
  // the form's own script builds it after the page: look again for a while
  if (waiting && attempt < 40 && document.querySelector('[data-form-root]')) setTimeout(() => formsDropdown(attempt + 1), 250);
}

const start = () => {
  document.querySelectorAll('#main > .luxe-split').forEach(stickColumn);
  formLinks();
  formsDropdown();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();

// the theme editor reloads a section in place: arrange the page again (the inline script's window.luxeInfoArrange)
['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((type) => {
  document.addEventListener(type, (event) => {
    if (typeof window.luxeInfoArrange === 'function') window.luxeInfoArrange(event.target instanceof Element ? event.target : undefined);
    start();
  });
});
